// @flow
import * as React from 'react';
import {
  responseTextBox as box,
  responseTextBoxFocused as boxFocused,
  responseTextBoxTextarea as textarea,
  responseTextBoxButtonGroup as buttonGroup,
  responseTextBoxButton as button,
  responseTextBoxOverlay as overlay,
  responseTextBoxLoader as loader,
  responseTextBoxAlert as alert,
} from './ResponseTextBox.scss';
import { ButtonGroup } from 'components';

type Props = {
  onChange: string => void,
  onSubmit: string => void,
  onCancel?: () => void,

  value: string,
  rows: number,
  status?: ?RequestStatus,
  placeholder?: ?string,
  buttonLabel?: ?string,
};

class ResponseTextBox extends React.PureComponent<Props, void> {
  el: ?HTMLDivElement;
  textArea: ?HTMLTextAreaElement;
  originalHeight: number;

  static defaultProps = {
    value: '',
    rows: 1,
    placeholder: 'Add a response...',
    buttonLabel: 'Submit',
  };

  hasValue() {
    return Boolean(
      typeof this.props.value === 'string' && this.props.value.trim()
    );
  }

  handleFocus = () => {
    this.el && this.el.classList.add(boxFocused);
  };

  handleBlur = () => {
    if (!this.hasValue() && this.el) {
      this.el.classList.remove(boxFocused);
      this.restoreOriginalHeight();

      this.props.onCancel && this.props.onCancel();
    }
  };

  handleSubmit = () => {
    this.props.onSubmit && this.props.onSubmit(this.props.value);
  };

  handleCancel = () => {
    if (this.props.onChange && this.el) {
      this.el.classList.remove(boxFocused);
      this.restoreOriginalHeight();
      this.props.onChange('');
    }

    this.props.onCancel && this.props.onCancel();
  };

  clear = () => {
    // we should maybe remove this
    // this is called from a parent component to reset the textbox
    this.handleCancel();
  };

  handleChange = (e: SyntheticEvent<HTMLTextAreaElement>) => {
    if (!this.textArea) return;
    this.adjustHeight();

    if (this.props.onChange) {
      this.props.onChange(e.currentTarget.value);
    }
  };

  adjustHeight() {
    if (!this.textArea) return;

    if (this.textArea.scrollHeight > this.textArea.clientHeight) {
      this.textArea.style.height = +this.textArea.scrollHeight + 'px';
    }
  }

  restoreOriginalHeight() {
    if (this.originalHeight && this.textArea) {
      this.textArea.style.height = `${this.originalHeight}px`;
    }
  }

  componentDidMount() {
    if (this.textArea) {
      this.originalHeight = this.textArea.scrollHeight;

      if (this.hasValue()) {
        this.handleFocus();
        this.adjustHeight();
      }
    }
  }

  renderErrorMessage(err: Error) {
    if (typeof err.status === 'number' && err.status === 403) {
      return 'You do not have the necessary permissions to make this change.';
    }

    return 'An error occurred while posting your response.';
  }

  render() {
    const { value, status, buttonLabel, ...rest } = this.props;

    return (
      <div
        className={box}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        ref={c => (this.el = c)}
        data-translate={true}
      >
        {status === 'pending' && (
          <div className={overlay}>
            <div className={loader} />
          </div>
        )}
        {status instanceof Error && value && (
          <div className={alert}>{this.renderErrorMessage(status)}</div>
        )}
        <textarea
          {...rest}
          value={value}
          ref={c => (this.textArea = c)}
          className={textarea}
          onChange={this.handleChange}
        />

        <div className={buttonGroup}>
          <ButtonGroup>
            <button onClick={this.handleCancel}>Cancel</button>
            <button disabled={!this.hasValue()} onClick={this.handleSubmit}>
              {status instanceof Error ? 'Retry' : buttonLabel}
            </button>
          </ButtonGroup>
        </div>
      </div>
    );
  }
}

export default ResponseTextBox;



// WEBPACK FOOTER //
// ./src/components/Common/ResponseTextBox.js